import Title_ru_RU              from '@/core/misc/decorators/Title_ru_RU';
import Title_ru_RU_Class        from "@/core/misc/decorators/Title_ru_RU_Class";
import TYPES                    from '@/core/services/Changelogs/types';

import AbstractShipmentDocument from '@/domains/Abstract/AbstractShipmentDocument';
import ShipmentPosition         from '@/domains/Shipments/ShipmentPosition';
import Core                     from '@/core/Core';
import ValidationError          from '@/core/misc/ValidationError';
import DateSimple               from '@/core/types/DateSimple';
import Currency                 from "@/domains/Currencies/Currency";
import CurrenciesService        from '@/domains/Currencies/CurrenciesService';
import Sum                      from '@/core/types/Sum';
import CurrencyRate             from '@/core/types/CurrencyRate';
import Assembly                 from "@/domains/Assemblies/Assembly";

import OrderPosition            from "@/domains/Orders/OrderPosition";

/**
 * Отгрузка
 */
@Title_ru_RU_Class('Отгрузка')
class Shipment extends AbstractShipmentDocument {

	static Events = {
		Created: 'shipment:created',
		Updated: 'shipment:updated',
		Deleted: 'shipment:deleted',
	} as const;

	static DOCUMENT_TYPE_LIST = [
		{id: 'ТОРГ12', name: 'ТОРГ-12'},
		{id: 'УПД', name: 'УПД'},
		{id: 'Invoice', name: 'Invoice'},
		{id: 'Товарная накладная', name: 'Товарная накладная'},
	];

	/** Причины по которым нельзя сделать то или иное действие */
	static REASON_UNAVAILABLE = {
		NO_EXIST_ENTITY_ON_BACKEND: 'Отгрузка ещё не создана',
		ALREADY_RECEIVED: 'Отгрузка уже принята',
		IS_BUYER: 'Только покупатель может создать платёж',
		NOT_ALLOWED: 'Доступ ограничен',
	} as const;

	// ---- Флаги ----

	/* Из чего будут браться позиции для отгрузки, либо из сборок, либо свободные позиции */
	public isShipmentByAssembly: boolean;

	/** Принята */
	@Title_ru_RU('Сверен с документами поставщика', TYPES.BOOLEAN)
	public isVerified: boolean;

	@Title_ru_RU('Принята', TYPES.BOOLEAN)
	public isReceived: boolean;

	// ---- Документ ----

	/* Поля для заполнения по ТОРГ12/УПД */
	@Title_ru_RU('Тип документа', TYPES.STRING)
	public documentType: { id: string; name: string } | null;

	@Title_ru_RU('Номер документа', TYPES.STRING)
	public documentNumber: string;

	private _documentDate: DateSimple;

	@Title_ru_RU('Дата отгрузки', TYPES.DATE)
	public shipmentDate: DateSimple;

	// ---- Сборка ----
	/* сборка по который была создана отгрузка */
	private assembly: Assembly | null;

	// ---- Валюта сделки ----

	private _orderCurrency: Currency | null;
	/* валюта сделки */
	private _orderCurrencyCode: string | null;

	@Title_ru_RU('Полная сумма', TYPES.SUM)
	/* Сумма в валюте документа */
	public sumFull: Sum;

	@Title_ru_RU('Сумма НДС', TYPES.SUM)
	/* Сумма НДС в валюте заказа */
	public vatSum: Sum;

	// ---- Валюта учёта ----
	/* Код валюты учета */
	private _accountingCurrencyCode: string = Core.currentPartner.country.localCurrency.code || Core.config.localCurrency.code;
	private _accountingCurrency: Currency | null;

	/* Курс валюты учета по отношению к валюте заказа */
	private _accountingCurrencyRate: CurrencyRate;

	/** курс может быть не всегда на дату документа */
	@Title_ru_RU('Дата курса', TYPES.DATE)
	private _accountingCurrencyRateDate: DateSimple;

	private _isCurrencyRateBasedAccountingCurrencyRateDate: boolean = false;

	/** Курс на дату документа */
	public currencyRateDocumentDate: CurrencyRate;

	/* Сумма в валюте учета на дату документа */
	@Title_ru_RU('Полная сумма в валюте учёта', TYPES.SUM)
	private _accountingSumFull: Sum;

	/* Сумма НДС в валюте учета */
	@Title_ru_RU('Сумма НДС в валюте учёта', TYPES.SUM)
	public accountingVatSum: Sum;

	@Title_ru_RU('Полная сумма в валюте учёта (рассчитанная автоматически)', TYPES.SUM)
	public accountingSumFullCalculated: Sum;

	@Title_ru_RU('Сумма НДС в валюте учёта (рассчитанная автоматически)', TYPES.SUM)
	public accountingVatSumCalculated: Sum;

	// ---- Доставка ----

	@Title_ru_RU('Трек номер', TYPES.STRING)
	public trackingNumber: string;
	/* Наименование ТК */
	public delivererName: string;

	@Title_ru_RU('Планируемая дата прибытия', TYPES.DATE)
	public arrivalDatePlanned: DateSimple;

	// ---- Внешние системы ----
	/* Номер техносклада */
	public externalNumber: string;
	public shipmentToBuyer: { id: number; shipmentDate?: DateSimple } | null;

	// ---- Приёмка ----

	public receiptDate: DateSimple;

	@Title_ru_RU('Участок прихода', TYPES.OBJECT)
	public receptionArea: { id: number; name?: string } | null;

	// ---- Плановые даты оплаты ----

	public plannedPaymentDates: DateSimple[] = [];

	// ---- Услуги ----
	/** В интерфейсе — «Дооценка»: туда попадают и бочки, и флексоформы. Позиции заказа у услуги нет */
	public services: ShipmentPosition[] = [];

  // ---

	static isShipment(entity: unknown): entity is Shipment {
		return entity instanceof Shipment;
	}

	static fromServer(data: any, shipment: Shipment = new Shipment()): Shipment {
		super.fromServer(data, shipment);

		// Позиции

		shipment.positions = Core.utils.hydrateArray(data.positions, ShipmentPosition.fromServer);
		shipment.services = Core.utils.hydrateArray(data.services, ShipmentPosition.fromServer);

		if (shipment.positions.length > 0) {
			shipment.isShipmentByAssembly = Boolean(Core.utils.isObject(shipment.positions[0].assemblyPosition)); // TODO зачем?
		}

		// Тип документа
		shipment.documentType = Shipment.DOCUMENT_TYPE_LIST.find(type => type.id === data.documentType) ?? null;

		// ---- Документ ----
		shipment.shipmentDate = DateSimple.fromServer(data.shipmentDate)!;
		shipment._documentDate = DateSimple.fromServer(shipment._documentDate)!;

		// ---- Валюта сделки ----
		shipment.orderCurrencyCode = data.orderCurrencyCode;

		shipment.sumFull = Core.utils.hydrate(data.sumFull, Sum.fromServer)!;
		shipment.vatSum  = Core.utils.hydrate(data.vatSum, Sum.fromServer)!;

		// ---- Валюта учёта ----
		shipment.accountingCurrencyCode = data.accountingCurrencyCode;

		shipment._accountingCurrencyRate     = Core.utils.hydrate(data.accountingCurrencyRate, CurrencyRate.fromServer)!;
		shipment._accountingCurrencyRateDate = DateSimple.fromServer(shipment._accountingCurrencyRateDate)!;
		shipment.currencyRateDocumentDate    = Core.utils.hydrate(data.currencyRateDocumentDate, CurrencyRate.fromServer)!;

		shipment._accountingSumFull          = Core.utils.hydrate(data.accountingSumFull, Sum.fromServer)!;
		shipment.accountingVatSum            = Core.utils.hydrate(data.accountingVatSum, Sum.fromServer)!;
		shipment.accountingSumFullCalculated = Core.utils.hydrate(data.accountingSumFullCalculated, Sum.fromServer)!;
		shipment.accountingVatSumCalculated  = Core.utils.hydrate(data.accountingVatSumCalculated, Sum.fromServer)!;

		// Флаг: курс не на дату документа
		if (shipment._accountingCurrencyRateDate && shipment._documentDate) {
			if (shipment._accountingCurrencyRateDate.getTime() !== shipment._documentDate.getTime()) {
				shipment._isCurrencyRateBasedAccountingCurrencyRateDate = true;
			}
		}

		// ---- Доставка ----
		shipment.arrivalDatePlanned = DateSimple.fromServer(data.arrivalDatePlanned)!;

		// ---- Внешние системы ----
		if (Core.utils.isObject(shipment.shipmentToBuyer)) {
			shipment.shipmentToBuyer.shipmentDate = DateSimple.fromServer(shipment.shipmentToBuyer.shipmentDate)!;
		}

		// ---- Приёмка ----
		shipment.receiptDate = DateSimple.fromServer(data.receiptDate)!;

		// ---- Плановые даты оплаты ----
		shipment.plannedPaymentDates = Core.utils.isNotEmptyArray(data.plannedPaymentDates)
			? data.plannedPaymentDates.map((d: any) => DateSimple.fromServer(d))
			: [];

		/** Обратная ссылка — последним шагом, когда документ уже гидрирован.
		 * Сеттер чистый, каскада не вызывает: значения позиций пришли с бэка
		 * и пересчитывать их фронтом не нужно. */
		shipment.positions.forEach((pos: ShipmentPosition) => pos.shipment = shipment);
		shipment.services.forEach((service: ShipmentPosition) => service.shipment = shipment);

		return shipment;
	}

	static fromNew(shipment = new Shipment()): Shipment {
		super.fromNew(shipment);

		return shipment;
	}

	static fromAssembly(assembly: any): Shipment {
		const shipment = new this();

		shipment.isShipmentByAssembly = true;
		shipment.assembly = assembly;

		shipment.buyer = assembly.buyer;
		shipment.buyerCompany = assembly.buyerCompany;
		shipment.buyerStore = assembly.buyerStore;

		shipment.seller = assembly.seller;
		shipment.sellerCompany = assembly.sellerCompany;

    if (shipment.sellerCompany?.country?.code !== undefined && shipment.sellerCompany?.country?.code !== null) {
      shipment.isForeign = shipment.sellerCompany.country.code !== Core.config.localCountry.code;
    }

		shipment.sellerStore = assembly.sellerStore;

		shipment.deliveryType = assembly.deliveryType;
		shipment.orderCategory = assembly.orderCategory;
		shipment.isOfferBased = assembly.isOfferBased;

		shipment.isVerified = false;
    shipment.isReceived = false;

    shipment.responsible = assembly.responsible!;

		/** Стоит гидрировать только те позиции, что отгружены не полностью.
		 * Позиции кладём все разом, только если они уживаются в одном документе.
		 * Иначе отгрузка остаётся с реквизитами сборки, позиции набирают вручную */
		let localPositions = assembly.positions
			.filter((assemblyPos: any) => assemblyPos.isShippedEntirely === false);

		localPositions = localPositions.map((assemblyPos: any) => ShipmentPosition.fromAssemblyPosition(assemblyPos));

		let isShippableEntirely = localPositions.length > 0;

		for (const pos of localPositions) {
			if (shipment.getReasonCannotAddPosition(pos) !== null) {
				isShippableEntirely = false;
			}
			else if (pos.orderCurrencyCode !== localPositions[0].orderCurrencyCode) {
				isShippableEntirely = false;
			}
			else if (pos.isVatIncluded !== localPositions[0].isVatIncluded) {
				isShippableEntirely = false;
			}
			else {
				// позиция совместима с документом и с первой позицией
			}
		}

		if (isShippableEntirely === true) {
			localPositions.forEach((pos: ShipmentPosition) => shipment.addPosition(pos));
		}
		else {
			Core.showNotification.info('Позиции сборки нельзя отгрузить одним документом — выберите, какие отгрузить');
		}

		shipment.calculateDependentSums();

		return shipment;
	}

	static fromOrderPosition(orderPosition: OrderPosition, shipment = new Shipment()): Shipment {
		super.fromOrderPosition(orderPosition, shipment);

		shipment.isShipmentByAssembly = false;
		shipment.isVerified = false;

    shipment.isReceived = false;

    shipment.orderCurrencyCode = orderPosition.currencyCode;

    shipment.responsible = orderPosition.responsible!;

		shipment.calculateDependentSums();

		return shipment;
	}

	/**
	 * Отгрузка под позиции заказа.
	 *
	 * Реквизиты берутся из первой позиции: в один документ отгружают позиции
	 * одной сделки, у остальных они те же. Позиции создаёт сама фабрика —
	 * снаружи собирать отгрузку по частям больше негде.
	 */
	static fromOrderPositions(orderPositions: OrderPosition[], shipment = new Shipment()): Shipment {
		this.fromOrderPosition(orderPositions[0], shipment);

		for (const orderPosition of orderPositions) {
			shipment.addPosition(ShipmentPosition.fromOrderPosition(orderPosition));
		}

		return shipment;
	}

	/** Заголовок документа: "ТОРГ12 №9207 от 16.12.2022" */
	get documentTitle(): string {
		const dateStr = this.documentDate?.toLocalString() ?? '';
		return `${this.documentType?.name ?? ''} №${this.documentNumber || ''} от ${dateStr}`;
	}

	get documentDate(): DateSimple {
		return this._documentDate;
	}

	set documentDate(date: DateSimple) {
		this._documentDate = date;
		this.loadAccountingCurrencyRate();
	}

	get isCurrencyRateBasedAccountingCurrencyRateDate(): boolean {
		return this._isCurrencyRateBasedAccountingCurrencyRateDate;
	}

	set isCurrencyRateBasedAccountingCurrencyRateDate(value: boolean) {
		this._isCurrencyRateBasedAccountingCurrencyRateDate = value;

		if (value === false) {
			this._accountingCurrencyRateDate = undefined;
		}

		if (value === false && this._documentDate) {
			this.loadAccountingCurrencyRate();
		}
	}

	get accountingCurrencyRateDate(): DateSimple {
		return this._accountingCurrencyRateDate;
	}

	set accountingCurrencyRateDate(value: DateSimple) {
		this._accountingCurrencyRateDate = value;
		this.loadAccountingCurrencyRate();
	}

	@Title_ru_RU('Курс валюты учёта', TYPES.NUMBER)
	get accountingCurrencyRate(): CurrencyRate {
		return this._accountingCurrencyRate;
	}

	set accountingCurrencyRate(currencyRate: CurrencyRate) {
		this._accountingCurrencyRate = currencyRate;

		this.positionsWithServices.forEach((pos: ShipmentPosition) => {
			if (pos.orderCurrencyCode === this.orderCurrencyCode) {
				pos.accountingCurrencyRate = this._accountingCurrencyRate;
			}
		});

		this.calculateDependentSums();
	}

	get accountingSumFull(): Sum {
		return this._accountingSumFull;
	}

	/**
	 * Ввод суммы в валюте учёта задаёт курс обратным счётом: курс = сумма учёта
	 * / сумма сделки. Отношение двух Sum — величина курса, поэтому результат
	 * заворачивается в CurrencyRate (scale 4).
	 */
	set accountingSumFull(value: Sum) {
		this._accountingSumFull = value;

		/** Курс обратным счётом невыводим при нулевой сумме сделки: делить не на что.
		 * Ошибки тут нет — уточнять курс не из чего, оставляем прежний. */
		if (!this.sumFull.isZero()) {
			this.accountingCurrencyRate = CurrencyRate.fromOperation(
				this._accountingSumFull.value.dividedBy(this.sumFull.value),
			);
		}
	}

	// ---- Валюта учёта ----
	// Пара код/объект: код ведущий, объект из словаря.

	get accountingCurrencyCode(): string { return this._accountingCurrencyCode; }
	set accountingCurrencyCode(value: string ) {
		this._accountingCurrencyCode = value;

		if (this._accountingCurrencyCode) {
			this._accountingCurrency = CurrenciesService.currencies.get(this._accountingCurrencyCode);
		} else {
			this._accountingCurrency = null;
		}
	}

	get accountingCurrency(): Currency | null { return this._accountingCurrency; }
	set accountingCurrency(value: Currency | null) {
		/** Ведущий — код: его сеттер подтянет объект из словаря. */
		// FIXME кинуть ошибку?
		this.accountingCurrencyCode = value?.code;
	}

	get orderCurrency(): Currency | null {
		return this._orderCurrency;
	}

	set orderCurrency(currency: Currency | null) {
		// Ведущий — код: его сеттер подтянет объект из словаря.
		this.orderCurrencyCode = currency?.code ?? null;


		this.positionsWithServices.forEach((pos: ShipmentPosition) => {
			pos.orderCurrency = this._orderCurrency;
		});

		/** Смена валюты сделки из формы — курс нужно перерезолвить. */
		if (this._orderCurrencyCode === this.accountingCurrencyCode) {
			this.accountingCurrencyRate = CurrencyRate.fromServer('1');
		} else {
			this.loadAccountingCurrencyRate();
		}
	}

	get orderCurrencyCode(): string | null {
		return this._orderCurrencyCode;
	}

	set orderCurrencyCode(currencyCode: string | null) {
		this._orderCurrencyCode = currencyCode;

		if (this._orderCurrencyCode) {
			this._orderCurrency = CurrenciesService.currencies.get(this._orderCurrencyCode);
		} else {
			this._orderCurrency = null;
		}
	}

	/** Все строки документа: суммы документа складываются из товаров и услуг */
	get positionsWithServices(): ShipmentPosition[] {
		return [...this.positions, ...this.services];
	}

	get cargoUnitsString(): string {
		if (!this.assembly && !this.cargoUnits) return '--';

    return `${this.cargoUnitsCount.formatAsInt()} ${Core.utils.string.pluralizeRu(this.cargoUnitsCount.toNumber(),
        'место', 'места', 'мест')}, `
      + `объем: ${this.cargoVolume.format()} м3, `
      + `нетто: ${this.cargoWeightNet.format()} кг, `
      + `брутто: ${this.cargoWeightGross.format()} кг`;
	}

	/**
	 * К проверкам совместимости из предка добавляется согласованность НДС:
	 * ставка позиции должна отвечать тому, платит ли НДС юрлицо продавца
	 * в этой отгрузке.
	 */
	/**
	 * Общие правила — в AbstractShipmentDocument.validatePosition, здесь только отгрузочные.
	 * Остаток и доставку проверяем только у свободной позиции: у позиции из сборки остаток считает сборка,
	 * а доставка платформой или покупателем как раз и отгружается через сборку.
	 */
	validatePosition(position: ShipmentPosition): void {
		const orderPosition = position.orderPosition;

		if (Core.utils.isNullOrUndefined(orderPosition) === false && Core.utils.isNullOrUndefined(position.assemblyPosition) === true) {
			if (orderPosition.qty.minus(orderPosition.shipmentStatus.assembled).minus(orderPosition.shipmentStatus.shipped).isPositive() === false) {
				throw new ValidationError(OrderPosition.ReasonUnavailable.NONE_AVAILABLE);
			}

			if (orderPosition.deliveryType?.id === 'byETP' || orderPosition.deliveryType?.id === 'byBuyer') {
				throw new ValidationError('Позиция с доставкой платформой или покупателем отгружается только через сборку');
			}
		}

		super.validatePosition(position);

		/** Отгрузка моновалютная и с одним типом НДС: их задаёт первая позиция */
		if (this.positions.length > 0 && position.orderPosition.currencyCode !== this.orderCurrencyCode) {
			throw new ValidationError('Не совпадает валюта у позиции и отгрузки');
		}

		if (this.positions.length > 0 && position.isVatIncluded !== this.positions[0].isVatIncluded) {
			throw new ValidationError('Не совпадает тип НДС у позиции и отгрузки');
		}

		ShipmentPosition.validateVatConsistency(position.orderPosition, this.sellerCompany);
	}

	addPosition(newPosition: ShipmentPosition): void {
		if (!ShipmentPosition.isShipmentPosition(newPosition)) {
			throw TypeError('Shipment.addPosition: аргумент не является ShipmentPosition');
		}

		super.addPosition(newPosition);

		newPosition.shipment = this;

		/** Первая позиция задаёт валюту и тип НДС документа */
		if (this.positions.length === 1) {
			if (this.isVatIncluded !== newPosition.isVatIncluded) {
				this.isVatIncluded = newPosition.isVatIncluded;
			}

			if (Core.utils.isNullOrUndefined(newPosition.orderCurrencyCode) === false && this.orderCurrencyCode !== newPosition.orderCurrencyCode) {
				this.orderCurrency = newPosition.orderCurrency;
			}
		}

		if (this._accountingCurrencyRate && newPosition.orderCurrencyCode === this.orderCurrencyCode) {
			newPosition.accountingCurrencyRate = this._accountingCurrencyRate;
		}

		newPosition.calculateSum();
	}

	/** Последнюю позицию удалить можно: пустую отгрузку не даст сохранить stateEntity карточки */
	deletePosition(shipmentPosition: ShipmentPosition | null = null, index: number | null = null): void {
		if (!shipmentPosition && index === null) {
			throw TypeError('Shipment.deletePosition: нужно передать либо позицию, либо индекс');
		}


		/** Если индекс был передан, то искать в коллекции нам уже не нужно,
		 * можно просто удалить элемент по индексу из массива.
		 *
		 * //TODO нужно перепроверить кейс, который описан ниже
		 * НО ВАЖНО ЗНАТЬ, нельзя передавать индекс позиции в диалоге добавления позиций,
		 * потому что тот индекс позиции из реестра предложенных позиций,
		 * те индексы не сочетаются с индексами позиций из документа */
		if (index === null) {
			if (shipmentPosition) {
				/* Если это createMode и отгрузка еще не была создана на беке, то проверяем на наличие id */
				if (shipmentPosition.id === null && this.isShipmentByAssembly) {
					/* В таком случае буду искать по assemblyPosition.id */
					index = this.positions.findIndex(
						(pos: ShipmentPosition) => pos.assemblyPosition.id === shipmentPosition.assemblyPosition.id,
					);
				} else if (shipmentPosition.id) {
					index = this.positions.findIndex(
						(pos: ShipmentPosition) => pos.id === shipmentPosition.id,
					);
				} else {
					/** В том случае если отгрузка свободная, и это СОЗДАНИЕ отгрузки, то айдишника у позиций нет,
					 * и assemblyPosition у них тоже нет, там есть только OrderPosition а значит по их айдишникам
					 * и нужно перебирать */
					index = this.positions.findIndex(
						(pos: ShipmentPosition) => pos.orderPosition.id === shipmentPosition.orderPosition.id,
					);
				}
			}
		}

		if (index !== null && index !== -1) {
			this.positions.splice(index, 1);
		} else {
			throw Error('Shipment.deletePosition: позиция не найдена');
		}

		/**
		 * Если при удалении мы удалили инициализирующую позицию (то есть первую),
		 * по которой были заданы поля документа, то некоторые реквизиты нужно обновить,
		 * такие, как валюта и курс
		 */
		if (this.positions.length === 0) {
			this.calculateDependentSums();
		} else {
			const basePosition = this.positions[0];

			if (this.isVatIncluded !== basePosition.isVatIncluded) {
				this.isVatIncluded = basePosition.isVatIncluded;
			}

			if (this.orderCurrencyCode !== basePosition.orderCurrencyCode) {
				this.orderCurrency = Core.utils.money.getCurrencyByCode(basePosition.orderCurrencyCode);
			} else {
				/**
				 * else нужен, чтобы не пересчитывать дважды суммы документа,
				 * тк когда меняется orderCurrency запускается длинная цепочка
				 * в конце которой будет вызвана функция пересчета зависимых сумм документа
				 */
				this.calculateDependentSums();
			}
		}
	}


	/**
	 * Валюта и признак «НДС включён» у услуги — документа: позиции заказа,
	 * из которой их взять, у неё нет. Документ без НДС — и ставка не нужна.
	 */
	addService(newService: ShipmentPosition): void {
		newService.shipment = this;
		newService.orderCurrencyCode = this.orderCurrencyCode;
		newService.isVatIncluded = this.isVatIncluded;

		if (this.isVatIncluded === null) {
			newService.vatRate = null;
		}

		if (this._accountingCurrencyRate) {
			newService.accountingCurrencyRate = this._accountingCurrencyRate;
		}

		this.services.push(newService);

		newService.calculateSum();
	}

	deleteService(service: ShipmentPosition): void {
		const index = this.services.indexOf(service);

		if (index === -1) {
			throw Error('Shipment.deleteService: услуга не найдена');
		}
		else {
			this.services.splice(index, 1);
			this.calculateDependentSums();
		}
	}

	async loadAccountingCurrencyRate(): Promise<void> {
		if (!this.orderCurrencyCode) return;

		let currencyDate: DateSimple;
		if (this.isCurrencyRateBasedAccountingCurrencyRateDate) {
			currencyDate = this._accountingCurrencyRateDate;
		}
		else {
			currencyDate = this._documentDate;
		}

		if (!currencyDate) return;

		// Сервис отдаёт курс числом — заворачиваем в тип на границе.
		const rate = CurrencyRate.fromServer(String(await CurrenciesService.getRate(
			this.orderCurrencyCode,
			this.accountingCurrencyCode,
			currencyDate,
		)));

		/* Не обращаюсь напрямую к приватному полю, тк хочу вызвать логику внутри сеттера */
		this.accountingCurrencyRate = rate;

		/** Нужно держать курс, чтобы сравнивать его с тем что есть в документе.
		 * UPD. немного коряво называется, тк теперь источник курса может быть
		 * _accountingCurrencyRateDate */
		this.currencyRateDocumentDate = rate;
	}

	calculateDependentSums(): void {
		this.calculateSum();
		this.calculateVatSum();
		this.calculateAccountingSum();
		this.calculateAccountingVatSum();
	}

	calculateSum(): void {
		this.sumFull = this.positionsWithServices.reduce((acc: Sum, pos: ShipmentPosition) => acc.plus(pos.sumFull), Sum.zero());
	}

	calculateVatSum(): void {
		this.vatSum = this.positionsWithServices.reduce((acc: Sum, pos: ShipmentPosition) => acc.plus(pos.vatSum), Sum.zero());
	}

	/**
	 * Агрегаты по позициям. Введённое пользователем значение позиции приоритетно
	 * над расчётным. Округление не нужно — Sum нормализован до своего scale.
	 */
	calculateAccountingSum(): void {
		this.accountingSumFullCalculated = this.positionsWithServices.reduce(
			(acc: Sum, pos: ShipmentPosition) => acc.plus(pos.accountingSumFull ?? pos.accountingSumFullCalculated),
			Sum.zero(),
		);
	}

	calculateAccountingVatSum(): void {
		this.accountingVatSumCalculated = this.positionsWithServices.reduce(
			(acc: Sum, pos: ShipmentPosition) => acc.plus(pos.accountingVatSum ?? pos.accountingVatSumCalculated),
			Sum.zero(),
		);
	}

  static reasonUnavailableDelete(shipment: Shipment | null | undefined): string | null {
		if (!shipment) {
			return null;
		}

		/** нельзя удалить отгрузку, если она еще не создана (этап создания отгрузки) */
		if (!shipment.id) return Shipment.REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND;

		/** нельзя удалить принятую отгрузку  */
		if (shipment.isReceived) return Shipment.REASON_UNAVAILABLE.ALREADY_RECEIVED;

		/** поставщик не может удалить прямой заказ */
		if (Core.currentPartner?.isSeller && !shipment.isOfferBased) return Shipment.REASON_UNAVAILABLE.NOT_ALLOWED;

		return null;
	}

	static reasonUnavailableEdit(shipment: Shipment | null | undefined): string | null {
		if (!shipment) {
			return null;
		}

		/** нельзя редактировать еще не созданную отгрузку */
		if (!shipment.id) return Shipment.REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND;

		return null;
	}

	static reasonUnavailableHistory(shipment: Shipment | null | undefined): string | null {
		if (!shipment) {
			return null;
		}

		/** история недоступна для еще не созданной отгрузки */
		if (!shipment.id) return Shipment.REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND;

		/** поставщику история недоступна */
		if (Core.currentPartner?.isSeller) return Shipment.REASON_UNAVAILABLE.NOT_ALLOWED;

		return null;
	}

	static reasonUnavailableCreatePayment(shipment: Shipment | null | undefined): string | null {
		if (!shipment) {
			return null;
		}

		/** нельзя создать платёж по еще не созданной отгрузке */
		if (!shipment.id) return Shipment.REASON_UNAVAILABLE.NO_EXIST_ENTITY_ON_BACKEND;

		/** платёж создаёт покупатель */
		if (Core.currentPartner?.isSeller) return Shipment.REASON_UNAVAILABLE.IS_BUYER;

		return null;
	}


	toString(): string {
		/** Пока тип документа основания не указан, представляемся типом самой
		 *  сущности: голый «№123» неизвестно чего не говорит ни о чём. */
		const type = this.documentType?.name ?? this.entityTypeName;
		const number = this.documentNumber ? `№${this.documentNumber}` : '';
		const date = this.documentDate ? `от ${Core.utils.date.format(this.documentDate)}` : '';
		const status = this.isVerified ? ' (Сверен)' : ' (Не сверен)';
		return `${type} ${number} ${date}${status}`.trim();
	}
}

export default Shipment;